import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import { CATEGORY_META, HIGHWAY_SHIELDS, CITY_LABELS, MAP_STYLES, DFW_CENTER, DFW_ZOOM, US_BOUNDS } from "@/data/mapConfig";
import { SERVICED_STATES, NO_COMPOUND_STATES, FULL_SERVICE_STATES, PHARMACY_COLORS } from "@/data/pharmacyStates";

function pinSVG(color) {
  return `<svg width="34" height="46" viewBox="0 0 34 46" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 0C7.6 0 0 7.6 0 17c0 12 17 29 17 29s17-17 17-29C34 7.6 26.4 0 17 0z" fill="${color}"/>
    <path d="M17 0C7.6 0 0 7.6 0 17c0 12 17 29 17 29s17-17 17-29C34 7.6 26.4 0 17 0z" fill="black" fill-opacity="0.06"/>
    <circle cx="17" cy="17" r="14" fill="${color}"/>
  </svg>`;
}

function createPinElement(loc, isActive) {
  const color = CATEGORY_META[loc.category]?.color || "#64748B";
  const el = document.createElement("div");
  el.className = "dfw-pin" + (isActive ? " active" : "");
  el.setAttribute("data-testid", `map-pin-${loc.id}`);
  el.style.width = "34px";
  el.style.height = "46px";
  el.innerHTML = `<div class="pin-inner">${pinSVG(color)}<span class="pin-num">${loc.number || ""}</span></div>`;
  return el;
}

function popupHTML(loc) {
  const meta = CATEGORY_META[loc.category] || { label: loc.category, color: "#64748B" };
  const title = loc.company || loc.name;
  const sub = loc.company ? loc.name : "";
  return `
    <div style="padding:14px 16px; min-width:220px; font-family:'Manrope',sans-serif;">
      <div style="display:inline-block; background:${meta.color}; color:#fff; font-size:10px; font-weight:700; letter-spacing:.5px; text-transform:uppercase; padding:3px 8px; border-radius:999px; margin-bottom:8px;">${meta.label}</div>
      <div style="font-family:'Outfit',sans-serif; font-weight:700; font-size:16px; color:#fff; margin-bottom:${sub ? "2px" : "4px"};">${title}</div>
      ${sub ? `<div style="font-size:12px; color:#93c5fd; font-weight:600; margin-bottom:6px;">${sub}</div>` : ""}
      <div style="font-size:13px; color:#cbd5e1; line-height:1.4;">${loc.address}</div>
      ${loc.pinned !== false && (loc.city || loc.zip) ? `<div style="font-size:13px; color:#cbd5e1; line-height:1.4;">${loc.city}${loc.city ? ", " : ""}${loc.state} ${loc.zip}</div>` : ""}
      ${loc.note ? `<div style="font-size:12px; color:#5eead4; margin-top:6px; font-weight:600;">✓ ${loc.note}</div>` : ""}
    </div>`;
}

export default function MapView({ locations, selected, onSelect, basemap = "light", pharmacyMode = false, searchPoint = null, nearest = null, focusBounds = null }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const staticMarkersRef = useRef([]);
  const popupRef = useRef(null);
  const loadedRef = useRef(false);
  const geojsonRef = useRef(null);
  const searchMarkerRef = useRef(null);
  const pharmacyRef = useRef(pharmacyMode);
  pharmacyRef.current = pharmacyMode;

  // Info-only services (Funding, Life Care Plans, DME, etc.) have no real coords -> show a
  // nationwide coverage view instead of flying to lat/lng 0,0.
  const infoView = !!(selected && selected.pinned === false && !pharmacyMode);

  // ---- Init map once ----
  useEffect(() => {
    if (mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLES[basemap],
      center: DFW_CENTER,
      zoom: DFW_ZOOM,
      attributionControl: { compact: true },
      preserveDrawingBuffer: true,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    mapRef.current = map;
    const syncZoomClass = () => map.getContainer().classList.toggle("dfw-zoomed-out", map.getZoom() < 8);
    map.on("zoom", syncZoomClass);
    syncZoomClass();
    popupRef.current = new maplibregl.Popup({ offset: 28, closeButton: true, maxWidth: "300px" });

    map.on("load", () => {
      loadedRef.current = true;
      HIGHWAY_SHIELDS.forEach((h) => {
        const el = document.createElement("div");
        el.className = `dfw-shield ${h.type}`;
        el.textContent = h.label;
        const m = new maplibregl.Marker({ element: el }).setLngLat([h.lng, h.lat]).addTo(map);
        staticMarkersRef.current.push(m);
      });
      CITY_LABELS.forEach((c) => {
        const el = document.createElement("div");
        el.className = "dfw-city";
        el.textContent = c.name;
        const m = new maplibregl.Marker({ element: el }).setLngLat([c.lng, c.lat]).addTo(map);
        staticMarkersRef.current.push(m);
      });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line

  // ---- Pharmacy coverage layers ----
  const addPharmacyLayers = async () => {
    const map = mapRef.current;
    if (!map) return;
    if (!geojsonRef.current) {
      try {
        geojsonRef.current = await fetch("/us-states.geojson").then((r) => r.json());
      } catch {
        return;
      }
    }
    if (!map.isStyleLoaded()) {
      map.once("idle", addPharmacyLayers);
      return;
    }
    if (map.getSource("us-states")) return;
    map.addSource("us-states", { type: "geojson", data: geojsonRef.current });
    map.addLayer({
      id: "states-fill",
      type: "fill",
      source: "us-states",
      paint: {
        "fill-color": [
          "match",
          ["get", "name"],
          NO_COMPOUND_STATES, PHARMACY_COLORS.noCompound,
          FULL_SERVICE_STATES, PHARMACY_COLORS.serviced,
          PHARMACY_COLORS.notServiced,
        ],
        "fill-opacity": ["match", ["get", "name"], SERVICED_STATES, 0.72, 0.1],
      },
    });
    map.addLayer({
      id: "states-line",
      type: "line",
      source: "us-states",
      paint: { "line-color": "#1e1b4b", "line-width": 0.9, "line-opacity": 0.55 },
    });
  };

  const removePharmacyLayers = () => {
    const map = mapRef.current;
    if (!map) return;
    ["states-fill", "states-line"].forEach((id) => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    if (map.getSource("us-states")) map.removeSource("us-states");
  };

  // ---- Nationwide coverage layers (all states lit up) for info-only services ----
  const addNationwideLayers = async () => {
    const map = mapRef.current;
    if (!map) return;
    if (!geojsonRef.current) {
      try {
        geojsonRef.current = await fetch("/us-states.geojson").then((r) => r.json());
      } catch {
        return;
      }
    }
    if (!map.isStyleLoaded()) {
      map.once("idle", addNationwideLayers);
      return;
    }
    if (map.getSource("us-states-nation")) return;
    map.addSource("us-states-nation", { type: "geojson", data: geojsonRef.current });
    map.addLayer({
      id: "nation-fill",
      type: "fill",
      source: "us-states-nation",
      paint: { "fill-color": "#6d3a82", "fill-opacity": 0.55 },
    });
    map.addLayer({
      id: "nation-line",
      type: "line",
      source: "us-states-nation",
      paint: { "line-color": "#291235", "line-width": 0.9, "line-opacity": 0.6 },
    });
  };

  const removeNationwideLayers = () => {
    const map = mapRef.current;
    if (!map) return;
    ["nation-fill", "nation-line"].forEach((id) => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    if (map.getSource("us-states-nation")) map.removeSource("us-states-nation");
  };

  // ---- Basemap switching ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      map.setStyle(MAP_STYLES[basemap]);
      map.once("idle", () => {
        if (pharmacyRef.current) addPharmacyLayers();
      });
    };
    if (loadedRef.current) apply();
    else map.once("load", apply);
  }, [basemap]); // eslint-disable-line

  // ---- Provider markers ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const render = () => {
      Object.values(markersRef.current).forEach((m) => m.remove());
      markersRef.current = {};
      if (pharmacyMode || infoView) return; // hide provider pins in coverage view
      locations.forEach((loc) => {
        if (loc.pinned === false || !loc.lat || !loc.lng) return; // info-only entries have no map pin
        const el = createPinElement(loc, selected?.id === loc.id);
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          onSelect(loc);
        });
        const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([loc.lng, loc.lat])
          .addTo(map);
        markersRef.current[loc.id] = marker;
      });
    };
    if (loadedRef.current) render();
    else map.once("load", render);
  }, [locations, selected, onSelect, pharmacyMode, infoView]);

  // ---- Toggle static markers (shields/cities) visibility in pharmacy mode ----
  useEffect(() => {
    staticMarkersRef.current.forEach((m) => {
      m.getElement().style.display = pharmacyMode || infoView ? "none" : "";
    });
  }, [pharmacyMode, infoView]);

  // ---- Pharmacy mode enter/exit ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pharmacyMode) {
      if (popupRef.current) popupRef.current.remove();
      addPharmacyLayers();
      map.fitBounds(US_BOUNDS, { padding: 40, duration: 1200 });
    } else {
      removePharmacyLayers();
      map.flyTo({ center: DFW_CENTER, zoom: DFW_ZOOM, duration: 1200 });
    }
  }, [pharmacyMode]); // eslint-disable-line

  // ---- Searched ZIP marker + fly-to ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (searchMarkerRef.current) {
      searchMarkerRef.current.remove();
      searchMarkerRef.current = null;
    }
    if (searchPoint && !pharmacyMode) {
      const el = document.createElement("div");
      el.className = "dfw-ziphome";
      el.setAttribute("data-testid", "zip-marker");
      el.innerHTML = `<span class="zip-pulse"></span><span class="zip-dot"></span><span class="zip-label">${searchPoint.zip}</span>`;
      searchMarkerRef.current = new maplibregl.Marker({ element: el }).setLngLat([searchPoint.lng, searchPoint.lat]).addTo(map);
      if (popupRef.current) popupRef.current.remove();
      if (nearest) {
        const b = new maplibregl.LngLatBounds([searchPoint.lng, searchPoint.lat], [searchPoint.lng, searchPoint.lat]);
        b.extend([nearest.lng, nearest.lat]);
        map.fitBounds(b, { padding: { top: 110, bottom: 90, left: 300, right: 120 }, maxZoom: 12.5, duration: 1100 });
      } else {
        map.flyTo({ center: [searchPoint.lng, searchPoint.lat], zoom: 10.2, duration: 1100 });
      }
    }
  }, [searchPoint, pharmacyMode, nearest?.id]); // eslint-disable-line

  // ---- State toggle: fit to that state's providers ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusBounds || pharmacyMode) return;
    if (popupRef.current) popupRef.current.remove();
    map.fitBounds(focusBounds.bounds, { padding: { top: 110, bottom: 90, left: 300, right: 120 }, maxZoom: 10, duration: 1200 });
  }, [focusBounds]); // eslint-disable-line

  // ---- Nationwide coverage view for info-only services ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (infoView) {
      if (popupRef.current) popupRef.current.remove();
      addNationwideLayers();
      map.fitBounds(US_BOUNDS, { padding: 40, duration: 1200 });
    } else {
      const had = map.getSource("us-states-nation");
      removeNationwideLayers();
      if (had && !pharmacyMode && !selected) {
        map.flyTo({ center: DFW_CENTER, zoom: DFW_ZOOM, duration: 1000 });
      }
    }
  }, [infoView]); // eslint-disable-line

  // ---- Fly to + popup on selection ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || pharmacyMode || infoView) return;
    if (!selected) {
      if (popupRef.current) popupRef.current.remove();
      return;
    }
    map.flyTo({ center: [selected.lng, selected.lat], zoom: Math.max(map.getZoom(), 11.5), speed: 1.1 });
    popupRef.current.setLngLat([selected.lng, selected.lat]).setHTML(popupHTML(selected)).addTo(map);
  }, [selected, pharmacyMode]);

  return (
    <>
      <div ref={containerRef} data-testid="dfw-map" style={{ position: "absolute", inset: 0 }} />
      {infoView && (
        <div
          data-testid="nationwide-banner"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-[#0B132B]/90 backdrop-blur-xl border border-white/15 rounded-xl shadow-2xl px-5 py-3 text-center pointer-events-none"
        >
          <p className="font-heading text-sm font-bold text-white">{selected.name}</p>
          <p className="text-xs text-slate-300 mt-0.5">{selected.address}</p>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-fuchsia-300 mt-1">
            {selected.note || "Nationwide"} · All 50 States
          </p>
        </div>
      )}
    </>
  );
}
